import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminApi,
  type AdminDashboardStats,
  type AdminAuditLog,
  type AdminUser,
  type SystemSetting,
} from "@/lib/api";
import { Spinner } from "@/components/ui/spinner";
import {
  Users,
  Building2,
  FileText,
  DollarSign,
  ArrowRight,
  UserPlus,
  Shield,
  Trash2,
  Pencil,
  KeyRound,
  Settings,
  LogIn,
  Activity,
  AlertTriangle,
  UserX,
  ClipboardList,
} from "lucide-react";
import { PageMeta, ADMIN_PAGE_META } from "@/lib/seo";

export function AdminDashboard() {
  const [stats, setStats] = useState<AdminDashboardStats | null>(null);
  const [recentLogs, setRecentLogs] = useState<AdminAuditLog[]>([]);
  const [recentUsers, setRecentUsers] = useState<AdminUser[]>([]);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [statsRes, logsRes, usersRes, settingsRes] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.listAuditLogs({ pageSize: 8, sortBy: "createdAt", sortOrder: "desc" }),
        adminApi.listUsers({ pageSize: 5, sortBy: "createdAt", sortOrder: "desc" }),
        adminApi.getSettings(),
      ]);

      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      } else {
        setError(statsRes.error?.message || "Kunde inte ladda statistik");
        return;
      }

      if (logsRes.success && logsRes.data) {
        setRecentLogs(logsRes.data.items);
      }

      if (usersRes.success && usersRes.data) {
        setRecentUsers(usersRes.data.items);
      }

      if (settingsRes.success && settingsRes.data) {
        const settingsMap = new Map<string, SystemSetting>();
        for (const s of settingsRes.data) settingsMap.set(s.key, s);
        setMaintenanceMode(settingsMap.get("maintenance_mode")?.value === true);
        setRegistrationEnabled(settingsMap.get("registration_enabled")?.value !== false);
      }
    } catch {
      setError("Kunde inte ansluta till servern");
      toast.error("Kunde inte ladda dashboard-data");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-700">{error}</p>
        <button onClick={loadDashboard} className="mt-3 text-sm text-red-600 hover:underline">
          Försök igen
        </button>
      </div>
    );
  }

  const statCards = [
    {
      label: "Användare",
      value: stats?.users.total ?? 0,
      subLabel: `+${stats?.users.new30Days ?? 0} senaste 30 dagarna`,
      icon: Users,
      iconBg: "bg-primary-100",
      iconColor: "text-primary-600",
      href: "/admin/users",
    },
    {
      label: "Företag",
      value: stats?.companies.total ?? 0,
      subLabel: `+${stats?.companies.new30Days ?? 0} senaste 30 dagarna`,
      icon: Building2,
      iconBg: "bg-emerald-100",
      iconColor: "text-emerald-600",
      href: "/admin/companies",
    },
    {
      label: "Årsredovisningar",
      value: stats?.reports.total ?? 0,
      subLabel: reportStatusSummary(stats?.reports.byStatus),
      icon: FileText,
      iconBg: "bg-amber-100",
      iconColor: "text-amber-600",
      href: "/admin/reports",
    },
    {
      label: "Prenumerationer",
      value: stats?.subscriptions.total ?? 0,
      subLabel: subscriptionStatusSummary(stats?.subscriptions.byStatus),
      icon: DollarSign,
      iconBg: "bg-purple-100",
      iconColor: "text-purple-600",
      href: "/admin/subscriptions",
    },
    {
      label: "Protokoll",
      value: stats?.protocols?.total ?? 0,
      subLabel: protocolStatusSummary(stats?.protocols?.byStatus),
      icon: ClipboardList,
      iconBg: "bg-cyan-100",
      iconColor: "text-cyan-600",
      href: "/admin/protocols",
    },
  ];

  return (
    <>
      <PageMeta title={ADMIN_PAGE_META.dashboard.title} noIndex />
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-2">Översikt av systemet</p>
        </div>

      {/* System warnings */}
      {maintenanceMode && (
        <Link
          to="/admin/settings"
          className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
        >
          <AlertTriangle size={20} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">Underhållsläge aktivt</p>
            <p className="text-xs text-amber-600">Alla API-anrop blockeras för icke-admin-användare. Klicka för att hantera.</p>
          </div>
        </Link>
      )}
      {!registrationEnabled && (
        <Link
          to="/admin/settings"
          className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
        >
          <UserX size={20} className="text-blue-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">Registrering avstängd</p>
            <p className="text-xs text-blue-600">Nya användare kan inte registrera sig. Klicka för att hantera.</p>
          </div>
        </Link>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        {statCards.map((card) => (
          <Link
            key={card.label}
            to={card.href}
            className="card group hover:shadow-md hover:border-primary-200 transition-all"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{card.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{card.value}</p>
                <p className="text-xs text-gray-400 mt-1">{card.subLabel}</p>
              </div>
              <div className={`w-12 h-12 ${card.iconBg} rounded-lg flex items-center justify-center`}>
                <card.icon size={24} className={card.iconColor} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Recent Activity */}
        <div className="lg:col-span-2 space-y-6">
          {/* Report Status Breakdown */}
          {stats?.reports.byStatus && Object.keys(stats.reports.byStatus).length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Årsredovisningar per status</h2>
                <Link to="/admin/reports" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Visa alla <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(stats.reports.byStatus).map(([status, count]) => (
                  <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-2xl font-bold text-gray-900">{count}</p>
                    <p className="text-sm text-gray-500 mt-1">{translateStatus(status)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          {recentLogs.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Senaste aktivitet</h2>
                <Link to="/admin/audit-logs" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Visa alla <ArrowRight size={14} />
                </Link>
              </div>
              <div className="space-y-1">
                {recentLogs.map((log) => (
                  <div
                    key={log.id}
                    className="flex items-start gap-3 py-2.5 px-2 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getActionStyle(log.action).bg}`}>
                      {getActionIcon(log.action)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">
                        <span className="font-medium">{log.userEmail || "System"}</span>
                        {" "}
                        <span className="text-gray-600">{translateAction(log.action)}</span>
                        {log.entityType && (
                          <span className="text-gray-400"> ({translateEntityType(log.entityType)})</span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400 mt-0.5">{formatRelativeTime(log.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right column: New Users */}
        <div className="space-y-6">
          {/* Recent Users */}
          {recentUsers.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Nya användare</h2>
                <Link to="/admin/users" className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1">
                  Visa alla <ArrowRight size={14} />
                </Link>
              </div>
              <div className="space-y-3">
                {recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-primary-700">
                        {getInitials(user.firstName, user.lastName, user.email)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName}`
                          : user.email}
                      </p>
                      {user.firstName && (
                        <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {formatShortDate(user.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* System Info */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Systemstatus</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Drift</span>
                {maintenanceMode ? (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-700">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Underhåll
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Aktiv
                  </span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Registrering</span>
                {registrationEnabled ? (
                  <span className="text-sm text-emerald-600">Öppen</span>
                ) : (
                  <span className="text-sm text-amber-600">Avstängd</span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Version</span>
                <span className="text-sm font-mono text-gray-500">0.4.3</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Regelmotor</span>
                <span className="text-sm text-gray-500">K2 (BFNAR 2016:10)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Helper functions
// ═══════════════════════════════════════════════════════════════════

function reportStatusSummary(byStatus?: Record<string, number>): string {
  if (!byStatus) return "";
  const draft = byStatus["draft"] ?? 0;
  const completed = byStatus["completed"] ?? 0;
  return `${draft} utkast, ${completed} klara`;
}

function subscriptionStatusSummary(byStatus?: Record<string, number>): string {
  if (!byStatus) return "";
  const active = byStatus["active"] ?? 0;
  const canceled = byStatus["canceled"] ?? 0;
  return `${active} aktiva, ${canceled} avbrutna`;
}

function protocolStatusSummary(byStatus?: Record<string, number>): string {
  if (!byStatus) return "";
  const draft = byStatus["draft"] ?? 0;
  const finalized = byStatus["finalized"] ?? 0;
  return `${draft} utkast, ${finalized} slutförda`;
}

function translateStatus(status: string): string {
  const map: Record<string, string> = {
    draft: "Utkast",
    in_progress: "Pågående",
    completed: "Klara",
    exported: "Exporterade",
    finalized: "Skickade",
  };
  return map[status] || status;
}

function translateAction(action: string): string {
  const map: Record<string, string> = {
    // User-initiated actions
    "user.login": "loggade in",
    "user.register": "registrerade sig",
    "user.update": "uppdaterade sin profil",
    "user.delete": "raderades",
    "user.restore": "återställdes",
    "user.password_reset": "återställde lösenord",
    "company.create": "skapade ett företag",
    "company.update": "uppdaterade ett företag",
    "company.delete": "raderade ett företag",
    "company.restore": "återställde ett företag",
    "report.create": "skapade en årsredovisning",
    "report.update": "uppdaterade en årsredovisning",
    "report.delete": "raderade en årsredovisning",
    "report.restore": "återställde en årsredovisning",
    "report.export": "exporterade en årsredovisning",
    "report.upload_sie": "laddade upp SIE-fil",
    // Admin actions
    "admin.user.update": "uppdaterade en användare",
    "admin.user.soft_delete": "raderade en användare",
    "admin.user.restore": "återställde en användare",
    "admin.user.reset_password": "återställde ett lösenord",
    "admin.user.assign_role": "tilldelade en roll",
    "admin.user.remove_role": "tog bort en roll",
    "admin.company.update": "uppdaterade ett företag",
    "admin.company.soft_delete": "raderade ett företag",
    "admin.company.restore": "återställde ett företag",
    "admin.report.soft_delete": "raderade en årsredovisning",
    "admin.report.restore": "återställde en årsredovisning",
    "admin.settings.update": "ändrade en inställning",
    "admin.role.create": "skapade en roll",
    "admin.role.update": "uppdaterade en roll",
    "admin.role.soft_delete": "raderade en roll",
    "admin.role.restore": "återställde en roll",
  };
  return map[action] || action;
}

function translateEntityType(type: string): string {
  const map: Record<string, string> = {
    user: "användare",
    company: "företag",
    report: "årsredovisning",
    role: "roll",
    setting: "inställning",
    system_setting: "inställning",
    annual_report: "årsredovisning",
    admin_role: "adminroll",
  };
  return map[type] || type;
}

function getActionStyle(action: string): { bg: string } {
  if (action.includes("delete")) return { bg: "bg-red-100" };
  if (action.includes("restore")) return { bg: "bg-emerald-100" };
  if (action.includes("create") || action.includes("register")) return { bg: "bg-blue-100" };
  if (action.includes("login")) return { bg: "bg-gray-100" };
  if (action.includes("role")) return { bg: "bg-amber-100" };
  if (action.includes("settings")) return { bg: "bg-purple-100" };
  return { bg: "bg-gray-100" };
}

function getActionIcon(action: string) {
  const size = 14;
  if (action.includes("delete")) return <Trash2 size={size} className="text-red-600" />;
  if (action.includes("restore")) return <Activity size={size} className="text-emerald-600" />;
  if (action.includes("create") || action.includes("register")) return <UserPlus size={size} className="text-blue-600" />;
  if (action.includes("login")) return <LogIn size={size} className="text-gray-600" />;
  if (action.includes("role")) return <KeyRound size={size} className="text-amber-600" />;
  if (action.includes("update") || action.includes("edit")) return <Pencil size={size} className="text-gray-600" />;
  if (action.includes("settings")) return <Settings size={size} className="text-purple-600" />;
  if (action.includes("admin")) return <Shield size={size} className="text-gray-600" />;
  return <Activity size={size} className="text-gray-500" />;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just nu";
  if (diffMin < 60) return `${diffMin} min sedan`;
  if (diffHours < 24) return `${diffHours} tim sedan`;
  if (diffDays < 7) return `${diffDays} dagar sedan`;
  return date.toLocaleDateString("sv-SE");
}

function formatShortDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffDays === 0) return "Idag";
  if (diffDays === 1) return "Igår";
  if (diffDays < 7) return `${diffDays} d sedan`;
  return date.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
}

function getInitials(firstName: string | null, lastName: string | null, email: string): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (firstName) return firstName[0].toUpperCase();
  return email[0].toUpperCase();
}
